import React, { useState, useEffect, useCallback } from "react";
import { COINS, CURRENCIES } from "../../utils/constants";
import { useApp } from "../../context/AppContext";
import { RefreshCw, TrendingUp, TrendingDown, Search } from "lucide-react";

const fmtNum = (n, d) => {
  if (!n && n !== 0) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: d });
};

export default function LivePrices() {
  const { currency } = useApp();
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [search, setSearch] = useState("");

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    try {
      const ids = COINS.map(c => c.id).join(",");
      const vs = CURRENCIES.map(c => c.code).join(",");
      const res = await fetch(
        "https://api.coingecko.com/api/v3/simple/price?ids=" + ids +
        "&vs_currencies=" + vs +
        "&include_24hr_change=true&include_market_cap=true"
      );
      const data = await res.json();
      setPrices(data);
      setLastUpdate(new Date());
    } catch (e) {
      console.error("Price fetch failed:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPrices();
    const iv = setInterval(fetchPrices, 60000);
    return () => clearInterval(iv);
  }, [fetchPrices]);

  const currObj = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  const allCrypto = COINS.filter(c => !c.isFiat);
  const filtered = search.trim()
    ? allCrypto.filter(c => c.symbol.toLowerCase().includes(search.toLowerCase()) || c.name.toLowerCase().includes(search.toLowerCase()))
    : allCrypto;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h3>Live Cryptocurrency Prices</h3>
          <span className="text-muted">
            {lastUpdate ? "Updated: " + lastUpdate.toLocaleTimeString("en-IN") : "Loading..."} · {allCrypto.length} coins
          </span>
        </div>
        <button className="btn-ghost" onClick={fetchPrices} disabled={loading}>
          <RefreshCw size={16} className={loading ? "spin" : ""} /> Refresh
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, padding: "8px 14px", maxWidth: 300 }}>
          <Search size={16} style={{ color: "#64748B" }} />
          <input type="text" placeholder="Search coins..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ background: "none", border: "none", outline: "none", color: "#E2E8F0", fontSize: 13, flex: 1, fontFamily: "inherit" }} />
        </div>
      </div>

      <div className="prices-grid">
        {filtered.map(coin => {
          const data = prices[coin.id];
          const price = data ? (data[currency] || data.inr || 0) : 0;
          const change = data ? (data[currency + "_24h_change"] || data.inr_24h_change || 0) : 0;
          const mcap = data ? (data[currency + "_market_cap"] || data.inr_market_cap || 0) : 0;
          const isUp = change >= 0;

          return (
            <div key={coin.id} className="price-card">
              <div className="price-top">
                <div className="coin-info">
                  <div className="coin-dot" style={{ background: coin.color + "22", color: coin.color }}>{coin.icon || coin.symbol.charAt(0)}</div>
                  <div>
                    <div className="coin-name">{coin.name}</div>
                    <div className="coin-sym">{coin.symbol}</div>
                  </div>
                </div>
                <div className={"change-badge " + (isUp ? "up" : "down")}>
                  {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {isUp ? "+" : ""}{change.toFixed(2)}%
                </div>
              </div>
              <div className="price-value">{currObj.symbol}{fmtNum(price, price < 10 ? 4 : price < 100 ? 2 : 0)}</div>
              {mcap > 0 && <div className="price-mcap">MCap: {currObj.symbol}{fmtNum(mcap / 1e9, 1)}B</div>}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#64748B" }}>No coins found</div>}
      </div>
    </div>
  );
}
